import {
  getActiveAccount,
  getStoredTeamsSsoToken,
  isEmbeddedContext,
  isInTeams,
  isMockAuthEnabled,
} from "@/lib/auth";
import { apiClient } from "@/lib/api-client";
import { queryKeys } from "@/lib/query-client";
import { IApiResponse } from "@/types/api";
import { IAuthenticatedUser, UserRole } from "@/types/auth";
import { useIsAuthenticated, useMsal } from "@azure/msal-react";
import { useQuery } from "@tanstack/react-query";
import axios from "axios";
import { useMemo } from "react";

const ROLE_SIMULATOR_KEY = "ai-registry-role-simulator";
const ORIGINAL_ADMIN_KEY = "ai-registry-original-admin";

interface IMeResponse {
  roles: UserRole[];
  /** Departments this Champion/Council member covers; empty = org-wide */
  departments: string[];
}

interface IUseCurrentUserReturn {
  user: IAuthenticatedUser | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  /** Departments the user covers per their role assignment; empty = org-wide/none */
  coveredDepartments: string[];
  /**
   * True while /api/me is in flight and roles are still token-derived.
   * Role-gated routes must wait for this before denying access — directory
   * roles may not exist in the token at all.
   */
  rolesResolving: boolean;
}

export function useCurrentUser(): IUseCurrentUserReturn {
  const { inProgress, accounts } = useMsal();
  const msalIsAuthenticated = useIsAuthenticated();

  // Check for Teams SSO authentication
  const teamsIsAuthenticated = isInTeams() && !!getStoredTeamsSsoToken();
  // Check for embedded context (SharePoint/Teams iframe) with SSO token
  const embeddedIsAuthenticated =
    isEmbeddedContext() && !!getStoredTeamsSsoToken();

  // In mock mode, Teams SSO, or embedded context, always return authenticated
  const isAuthenticated =
    isMockAuthEnabled ||
    msalIsAuthenticated ||
    teamsIsAuthenticated ||
    embeddedIsAuthenticated;

  const baseUser = useMemo<IAuthenticatedUser | null>(() => {
    if (import.meta.env.DEV) {
      console.log(
        "[useCurrentUser] Computing user, isAuthenticated:",
        isAuthenticated,
        "isMockAuthEnabled:",
        isMockAuthEnabled
      );
    }

    if (!isAuthenticated) {
      if (import.meta.env.DEV) {
        console.log("[useCurrentUser] Not authenticated");
      }
      return null;
    }

    // Get account from our auth module (handles mock mode)
    const account = getActiveAccount();
    if (!account) {
      if (import.meta.env.DEV) {
        console.warn(
          "[useCurrentUser] No account found despite being authenticated",
          {
            isMockAuthEnabled,
            msalIsAuthenticated,
            accountsCount: accounts.length,
          }
        );
      }
      return null;
    }

    if (import.meta.env.DEV) {
      const idTokenClaims = account.idTokenClaims as
        | Record<string, unknown>
        | undefined;
      console.log("[useCurrentUser] Account retrieved:", {
        hasAccount: !!account,
        accountName: account.name,
        accountUsername: account.username,
        hasIdTokenClaims: !!account.idTokenClaims,
        idTokenClaimsName: idTokenClaims?.name,
      });
    }

    // Roles come from the in-app directory via /api/me (merged in below).
    // Until that resolves, everyone is an End User — token group claims are
    // no longer used for authorization. In mock mode the mock account's
    // groups carry role values directly.
    const idTokenClaims = account.idTokenClaims as
      | Record<string, unknown>
      | undefined;
    let roles: UserRole[] = [UserRole.END_USER];
    if (isMockAuthEnabled) {
      const groups = (idTokenClaims?.groups as string[] | undefined) ?? [];
      const mockRoles = groups.filter((g): g is UserRole =>
        Object.values(UserRole).includes(g as UserRole)
      );
      if (mockRoles.length > 0) {
        roles = mockRoles;
      }
    }

    // Check for role simulation (dev/admin feature)
    // Allow in dev mode, mock mode, OR for admins in production
    const isAdmin = roles.includes(UserRole.ADMIN);
    const isOriginalAdmin = (() => {
      try {
        return localStorage.getItem(ORIGINAL_ADMIN_KEY) === "true";
      } catch {
        return false;
      }
    })();

    // Allow role simulation if: dev mode, mock mode, OR admin (original or current)
    if (
      import.meta.env.DEV ||
      isMockAuthEnabled ||
      isAdmin ||
      isOriginalAdmin
    ) {
      try {
        const simulatedRole = localStorage.getItem(ROLE_SIMULATOR_KEY);
        if (
          simulatedRole &&
          Object.values(UserRole).includes(simulatedRole as UserRole)
        ) {
          // Override roles with simulated role
          roles = [simulatedRole as UserRole];
          if (import.meta.env.DEV) {
            console.log(
              `[useCurrentUser] Role simulation active: ${simulatedRole}`
            );
          }
        }
      } catch {
        // Ignore errors reading from localStorage
      }
    }

    // Extract name from token claims first, then fall back to account properties
    // Priority: idTokenClaims.name > account.name > preferred_username > username
    const nameFromClaims = idTokenClaims?.name as string | undefined;
    const preferredUsername = idTokenClaims?.preferred_username as
      | string
      | undefined;
    const emailFromClaims = idTokenClaims?.email as string | undefined;

    // Ensure we get a valid string name
    let userName: string;
    if (
      nameFromClaims &&
      typeof nameFromClaims === "string" &&
      nameFromClaims.trim()
    ) {
      userName = nameFromClaims.trim();
    } else if (
      account.name &&
      typeof account.name === "string" &&
      account.name.trim()
    ) {
      userName = account.name.trim();
    } else if (preferredUsername && typeof preferredUsername === "string") {
      // Extract name from email if needed
      const emailPart = preferredUsername.split("@")[0];
      userName = emailPart
        ? emailPart.replace(/[._]/g, " ").trim() || "User"
        : "User";
    } else if (account.username && typeof account.username === "string") {
      const emailPart = account.username.split("@")[0];
      userName = emailPart
        ? emailPart.replace(/[._]/g, " ").trim() || "User"
        : "User";
    } else {
      userName = "User";
    }

    const userEmail =
      (preferredUsername ?? emailFromClaims ?? account.username) || "";

    if (import.meta.env.DEV) {
      console.log("[useCurrentUser] Extracted user info:", {
        nameFromClaims,
        accountName: account.name,
        preferredUsername,
        accountUsername: account.username,
        finalName: userName,
        account: {
          localAccountId: account.localAccountId,
          name: account.name,
          username: account.username,
        },
        idTokenClaims: {
          name: idTokenClaims?.name,
          preferred_username: idTokenClaims?.preferred_username,
        },
      });
    }

    const baseUser: IAuthenticatedUser = {
      userId: account.localAccountId,
      email: userEmail,
      name: userName,
      tenantId: account.tenantId,
      roles,
    };

    if (import.meta.env.DEV) {
      console.log("[useCurrentUser] Base user object:", {
        userId: baseUser.userId,
        name: baseUser.name,
        email: baseUser.email,
        tenantId: baseUser.tenantId,
        roles: baseUser.roles,
      });
    }

    return baseUser;
  }, [isAuthenticated, msalIsAuthenticated, accounts.length]);

  // Fetch authoritative roles (and covered departments) from /api/me —
  // the in-app directory is the sole role source. Users render as End
  // Users until this resolves. Shared via React Query so all hook
  // instances reuse one request.
  const meQuery = useQuery({
    queryKey: queryKeys.users.current(),
    queryFn: async (): Promise<IMeResponse> => {
      const response = await apiClient.get<IApiResponse<IMeResponse>>("/me");
      return response.data.data;
    },
    enabled: !!baseUser && !isMockAuthEnabled,
    staleTime: 60 * 1000,
    retry: (failureCount, error) => {
      // Don't retry auth failures — token roles remain the fallback
      if (axios.isAxiosError(error)) {
        const status = error.response?.status;
        if (status === 401 || status === 403) return false;
      }
      return failureCount < 2;
    },
  });

  const apiRoles =
    meQuery.data && Array.isArray(meQuery.data.roles) && meQuery.data.roles.length > 0
      ? meQuery.data.roles
      : null;
  const coveredDepartments = useMemo(
    () => meQuery.data?.departments ?? [],
    [meQuery.data],
  );
  // isLoading = pending AND fetching, so this is false when the query is
  // disabled (mock mode / signed out) and once a result or error lands
  const rolesResolving = meQuery.isLoading;

  // Merge base user with API-resolved roles.
  // API roles win over token-derived roles, except while local role
  // simulation is active (baseUser already reflects the simulated role).
  const user = useMemo<IAuthenticatedUser | null>(() => {
    if (!baseUser) return null;

    const simulationActive = (() => {
      try {
        if (!localStorage.getItem(ROLE_SIMULATOR_KEY)) return false;
        const isOriginalAdmin = localStorage.getItem(ORIGINAL_ADMIN_KEY) === "true";
        return (
          import.meta.env.DEV ||
          isMockAuthEnabled ||
          isOriginalAdmin ||
          baseUser.roles.includes(UserRole.ADMIN)
        );
      } catch {
        return false;
      }
    })();

    if (apiRoles && !simulationActive) {
      return { ...baseUser, roles: apiRoles };
    }

    return baseUser;
  }, [baseUser, apiRoles]);

  return {
    user,
    isLoading: isMockAuthEnabled ? false : inProgress !== "none",
    isAuthenticated,
    coveredDepartments,
    rolesResolving,
  };
}
